import type ApolloClient from 'apollo-client';
import type { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import { functionWithFG, functionWithCondition } from '@atlaskit/platform-feature-flags-react';

import { type Product } from '../methods/useProductFetchClient';
import { graphql, fetchQuery, type Environment } from 'react-relay';
import { createAtlAttributionHeader } from '../../../utils';
import { type getTenantRegionQuery } from './__generated__/getTenantRegionQuery.graphql';
import { fg } from '@atlaskit/platform-feature-flags';

let tenantRegionResult: Promise<string | null> | undefined;

// Exported for testing purposes only
export const resetTenantRegionResult = () => {
	tenantRegionResult = undefined;
};

const getTenantRegionRelay = ({
	product,
	cloudId,
	relayEnvironment,
}: Args): Promise<string | null> => {
	if (!!tenantRegionResult) {
		return tenantRegionResult;
	}

	if (!relayEnvironment || !cloudId) {
		return Promise.resolve(null);
	}

	try {
		const observable = fetchQuery<getTenantRegionQuery>(
			relayEnvironment,
			graphql`
				query getTenantRegionQuery($cloudIds: [ID!], $product: String!) {
					tenantContexts(cloudIds: $cloudIds) {
						activationIdByProduct(product: $product) {
							targetRegion
						}
					}
				}
			`,
			{
				cloudIds: [cloudId],
				product,
			},
			{
				networkCacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader(),
						},
					},
				},
			},
		);

		tenantRegionResult = new Promise<string | null>((resolve) => {
			observable.subscribe({
				next: (data) => {
					resolve(data?.tenantContexts?.[0]?.activationIdByProduct?.targetRegion ?? null);
				},
				error: () => {
					resolve(null);
				},
			});
		});

		return tenantRegionResult;
	} catch {
		return Promise.resolve(null);
	}
};

export const tcsByCloudIdQuery: DocumentNode = gql`
	query tcsByCloudId($cloudIds: [ID!], $product: String!) {
		tenantContexts(cloudIds: $cloudIds) {
			activationIdByProduct(product: $product) {
				targetRegion
			}
		}
	}
`;

interface TcsByCloudIdQueryVariables {
	cloudIds: string[];
	product: string;
}

interface TcsByCloudIdQueryData {
	tenantContexts: {
		activationIdByProduct: {
			targetRegion: string;
		};
	}[];
}

type Args = {
	client?: ApolloClient<object | undefined>;
	cloudId?: string;
	product: Product;
	relayEnvironment?: Environment;
};

const getTenantRegionApollo = ({ product, client, cloudId }: Args): Promise<string | null> => {
	if (!!tenantRegionResult) {
		return tenantRegionResult;
	}

	if (!cloudId || !client) {
		return Promise.resolve(null);
	}

	tenantRegionResult = client
		.query<TcsByCloudIdQueryData, TcsByCloudIdQueryVariables>({
			query: tcsByCloudIdQuery,
			variables: {
				cloudIds: [cloudId],
				product,
			},
		})
		.then(({ data }) => {
			return data?.tenantContexts[0].activationIdByProduct.targetRegion || null;
		})
		.catch(() => null);

	return tenantRegionResult;
};

export const getTenantRegionImpl = functionWithCondition<(args: Args) => Promise<string | null>>(
	() => fg('forge-ui-add-relay-tcs-by-cloud-id-query'),
	getTenantRegionRelay,
	getTenantRegionApollo,
);

export const getTenantRegion = functionWithFG(
	'forge-ui-enable-region-steering',
	getTenantRegionImpl,
	() => Promise.resolve(null),
);
